import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
    adminListSubmissions,
    adminLogout,
    adminMe,
    adminRequestOutreach,
    adminWarmLeads,
} from "../lib/admin";
import { track } from "../lib/analytics";

import "./Admin.css";

const STATUS_OPTIONS = [
    ["all", "All"],
    ["in_progress", "In progress"],
    ["submitted_to_laguna", "Submitted to Laguna"],
    ["submitted_to_massmutual", "Submitted to MassMutual"],
    ["policy_issued", "Policy issued"],
    ["returned_to_employer", "Returned to employer"],
    ["abandoned", "Abandoned"],
];


export default function AdminSubmissions() {
    const navigate = useNavigate();
    const [me, setMe] = useState(null);
    const [rows, setRows] = useState([]);
    const [statusFilter, setStatusFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [warm, setWarm] = useState({ rows: [], threshold: 3 });
    const [outreachSent, setOutreachSent] = useState({});

    const load = useCallback(async (filter) => {
        setLoading(true);
        try {
            const [subs, wl] = await Promise.all([
                adminListSubmissions(filter),
                adminWarmLeads(),
            ]);
            setRows(subs.rows || []);
            setWarm(wl);
        } catch (err) {
            if (err?.response?.status === 401) {
                navigate("/admin/login");
                return;
            }
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        document.title = "Laguna · submissions";
        track("admin_submissions_view", {});
        (async () => {
            try {
                const meInfo = await adminMe();
                setMe(meInfo);
                await load(statusFilter);
            } catch (_) {
                navigate("/admin/login");
            }
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onFilterChange = async (v) => {
        setStatusFilter(v);
        await load(v);
    };

    const onSignOut = async () => {
        await adminLogout();
        navigate("/admin/login");
    };

    const onRequestOutreach = async (proposal_id) => {
        try {
            await adminRequestOutreach(proposal_id);
            setOutreachSent((s) => ({ ...s, [proposal_id]: true }));
            track("admin_warm_lead_outreach", { proposalId: proposal_id });
        } catch (_) { /* swallow — button will remain clickable */ }
    };

    const fmt = (iso) => {
        if (!iso) return "—";
        try {
            return new Date(iso).toLocaleDateString(undefined, {
                month: "short", day: "numeric", year: "numeric",
            });
        } catch { return iso; }
    };

    return (
        <div className="admin-page" data-testid="admin-submissions">
            <nav className="admin-nav">
                <div style={{ display: "flex", alignItems: "center", gap: "2rem", flexWrap: "wrap" }}>
                    <div className="logo-mark" onClick={() => navigate("/")}>
                        Laguna<span>.</span>
                    </div>
                    <div className="admin-bread">
                        Admin<span className="current">Submissions</span>
                    </div>
                    {/* Phase A5 — admin subnav for cross-feature navigation */}
                    <div className="admin-subnav">
                        <button
                            type="button"
                            className="active"
                            data-testid="admin-subnav-submissions"
                        >
                            Submissions
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate("/admin/users")}
                            data-testid="admin-subnav-users"
                        >
                            Users
                        </button>
                    </div>
                </div>
                <div className="admin-session">
                    <span>{me?.email}</span>
                    <button
                        className="signout"
                        onClick={onSignOut}
                        data-testid="admin-signout"
                    >
                        Sign out
                    </button>
                </div>
            </nav>

            <div className="admin-wrap">
                <div className="section-kicker">Submissions console</div>
                <h1 className="admin-h1">
                    Employer <em>applications.</em>
                </h1>
                <p className="admin-sub">
                    Everything that's come through — quote, proposal in
                    progress, or fully submitted. Click a row to review + act.
                </p>

                {/* WARM LEADS PANEL */}
                <div className="warm-panel" data-testid="warm-leads-panel">
                    <div className="section-kicker">Warm leads</div>
                    <div className="section-h">
                        Proposals viewed {warm.threshold}+ times, no
                        application started
                    </div>
                    {warm.rows.length === 0 ? (
                        <div className="warm-empty" data-testid="warm-empty">
                            Nothing heating up right now.
                        </div>
                    ) : (
                        <div className="warm-panel-row">
                            {warm.rows.slice(0, 5).map((w) => (
                                <div className="warm-card" key={w.proposal_id} data-testid={`warm-card-${w.proposal_id}`}>
                                    <div className="name">{w.company_name || "—"}</div>
                                    <div className="row">
                                        <span className="views">{w.view_count} views</span>
                                        <span>· last {fmt(w.last_viewed_at)}</span>
                                    </div>
                                    <div className="row">
                                        <span>{w.employee_count} employees</span>
                                        {w.situs_state && <span>· {w.situs_state}</span>}
                                    </div>
                                    <div className="row">
                                        <span>{w.contact_name || "—"}</span>
                                        <span>· {w.contact_email || "—"}</span>
                                    </div>
                                    <div className="cta-row">
                                        <button
                                            type="button"
                                            onClick={() => onRequestOutreach(w.proposal_id)}
                                            className={outreachSent[w.proposal_id] ? "req-outreach-done" : ""}
                                            disabled={!!outreachSent[w.proposal_id]}
                                            data-testid={`warm-outreach-${w.proposal_id}`}
                                        >
                                            {outreachSent[w.proposal_id] ? "Outreach requested" : "Request outreach"}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* FILTER */}
                <div className="subs-filter-row">
                    <div className="section-kicker" style={{ marginBottom: 0 }}>Filter</div>
                    <select
                        className="subs-filter"
                        value={statusFilter}
                        onChange={(e) => onFilterChange(e.target.value)}
                        data-testid="admin-status-filter"
                    >
                        {STATUS_OPTIONS.map(([v, l]) => (
                            <option key={v} value={v}>{l}</option>
                        ))}
                    </select>
                    <span className="muted" style={{ fontSize: "0.8rem" }}>
                        {loading ? "Loading…" : `${rows.length} result${rows.length === 1 ? "" : "s"}`}
                    </span>
                </div>

                {/* TABLE */}
                <div className="subs-table-wrap">
                    <table className="subs-table" data-testid="admin-subs-table">
                        <thead>
                            <tr>
                                <th>Company</th>
                                <th>Headcount</th>
                                <th>Situs</th>
                                <th>Status</th>
                                <th>Submitted</th>
                                <th>Admin contact</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && !loading && (
                                <tr>
                                    <td colSpan={6} style={{ textAlign: "center", padding: "2rem", fontStyle: "italic", color: "var(--muted)" }}>
                                        No applications match this filter yet.
                                    </td>
                                </tr>
                            )}
                            {rows.map((r) => (
                                <tr
                                    key={r.application_id}
                                    onClick={() => navigate(`/admin/submissions/${r.application_id}`)}
                                    data-testid={`admin-row-${r.application_id}`}
                                >
                                    <td>{r.company_name}</td>
                                    <td className="num">{r.employee_count ?? "—"}</td>
                                    <td>{r.situs_state || "—"}</td>
                                    <td>
                                        <span className={`status-pill ${r.status}`}>{r.status?.replace(/_/g, " ")}</span>
                                    </td>
                                    <td className="num">{fmt(r.submitted_at)}</td>
                                    <td>{r.admin_contact_email || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
